/**
 * Home Dashboard bundle dispatcher.
 *
 * Included LAST in js/assessments/dist/home-dashboard-bundle.min.js (built by
 * the homeDashboard task in gulpfile.js). The bundle order is:
 *
 *   1. sharedChartUtilities  (formatState, calculateMaturityScore, …)
 *   2. web-assessment-charts-crfs2021.js  ─┐  each year-specific file
 *   3. web-assessment-charts-crfs2022.js   │  registers a jQuery(document)
 *   4. … through 2026.js                  ─┘  .ready that:
 *                                              · sets WebAssessmentCharts.fetchDataAndCalculateScores
 *                                              · sets WebAssessmentCharts.fetchDataAndCalculateScores{YYYY}
 *                                              · binds its own change handlers on .project-selection
 *                                                and .dashboard-selection
 *   5. THIS FILE
 *
 * Without this file we inherit two problems on the home page:
 *
 *   A. WebAssessmentCharts.fetchDataAndCalculateScores is whichever year was
 *      bundled last (2026), so picking a 2024 project would still fetch
 *      2024-against-2026 quality data and the local closure vars in 2024.js
 *      stay empty → chart renders zeroes.
 *
 *   B. Each year file's $('.project-selection').on('change', …) handler
 *      fires every time a project is picked. With six handlers stacked on
 *      one event, only the year matching the project actually populates its
 *      own closure state; the rest run with stale/empty state and the
 *      .dashboard-selection 'CRFS' switch case in each file then either
 *      no-ops or overwrites the table with zeroes (last handler wins).
 *
 * This dispatcher fixes both by:
 *
 *   · Always defining fetchDataAndCalculateScoresDispatcher (the existing
 *     framework-dispatchers.js scopes it to the web-assessment-tool page).
 *   · Detaching every .project-selection and .dashboard-selection change
 *     handler the year files attached, so only our home-dashboard.js code
 *     drives the widget. We can do this safely because the home page never
 *     re-uses dashboard-view.php — those filters live solely in our widget.
 */
(function ($) {
    'use strict';

    if (!window.WebAssessmentCharts) {
        window.WebAssessmentCharts = {};
    }
    if (typeof WebAssessmentCharts.remapProcessedItemsForTargetYear !== 'function' &&
        typeof window.remapProcessedItemsForTargetYear === 'function') {
        WebAssessmentCharts.remapProcessedItemsForTargetYear = window.remapProcessedItemsForTargetYear;
    }

    /**
     * Same year-aware mapping as js/markcomplete/framework-dispatchers.js
     * but defined unconditionally (no `if (body.hasClass('page-template-page-web-assessment-tool'))`
     * gate). The home page uses body class `page-template-page-home-php` so
     * the existing dispatcher otherwise wouldn't activate.
     */
    function frameworkSlugToYear(slug) {
        slug = String(slug || '').toLowerCase();
        if (slug === 'crf-s-2021') return '2021';
        if (slug === 'crf-s-2022') return '2022';
        if (slug === 'crf-s-2023') return '2023';
        if (slug === 'crf-s' || slug === 'crf-s-2024') return '2024';
        if (slug === 'crf-s-2025') return '2025';
        if (slug === 'crf-s-2026' ||
            slug === 'crf-s-appsec-2026' ||
            slug === 'crf-s-gov-2026' ||
            slug === 'crf-s-hyg-2026' ||
            slug === 'crf-s-smb-2026') return '2026';
        return null;
    }

    function isLegacyAssessmentYear(year) {
        var y = String(year || '');
        return y === '2021' || y === '2022' || y === '2023';
    }

    /**
     * When cross-version scoring loads target-year quality JSON for 2026, the
     * same sub-version topic/prefix filters as web-assessment-charts-crfs2026.js
     * must apply so organizeItems + crfDomain match the selected dashboard.
     *
     * @param {Array} rawItems qualityData.CRF.Items
     * @param {string} fwSlug normalized framework slug (e.g. crf-s-hyg-2026)
     * @returns {Array}
     */
    function filter2026TargetItemsBySlug(rawItems, fwSlug) {
        if (!Array.isArray(rawItems)) return rawItems || [];
        var slug = String(fwSlug || '').toLowerCase();
        if (slug === 'crf-s-appsec-2026') {
            var appSecTopics = [
                'software development standardization management',
                'software development management',
                'software development standardization',
                'software development vulnerability management',
                'software development operations (devops)',
                'software development operations management'
            ];
            return rawItems.filter(function (item) {
                var topic = (item.safeguardtopic && item.safeguardtopic.S)
                    ? item.safeguardtopic.S.trim().toLowerCase() : '';
                return appSecTopics.indexOf(topic) !== -1;
            });
        }
        if (slug === 'crf-s-gov-2026') {
            var govPrefixes = ['PRG', 'SAF', 'EDU', 'IMP', 'VAL', 'TPR', 'COM'];
            return rawItems.filter(function (item) {
                var id = (item.safeguardid && item.safeguardid.S) ? item.safeguardid.S : '';
                var prefix = id.split('-')[0].toUpperCase();
                return govPrefixes.indexOf(prefix) !== -1;
            });
        }
        if (slug === 'crf-s-hyg-2026') {
            var hygPrefixes = ['AST', 'RES', 'PHY', 'ID', 'PAM', 'AM', 'LOG', 'SYS', 'SW', 'VUL', 'NDM', 'PNA', 'INA', 'EM', 'SDV'];
            return rawItems.filter(function (item) {
                var id = (item.safeguardid && item.safeguardid.S) ? item.safeguardid.S : '';
                var prefix = id.split('-')[0].toUpperCase();
                return hygPrefixes.indexOf(prefix) !== -1;
            });
        }
        if (slug === 'crf-s-smb-2026') {
            var smbPrefixes = ['SAF', 'EDU', 'AST', 'RES', 'PHY', 'ID', 'PAM', 'AM', 'LOG', 'SYS', 'SW', 'VUL', 'NDM', 'PNA', 'INA', 'CSP', 'EM'];
            return rawItems.filter(function (item) {
                var id = (item.safeguardid && item.safeguardid.S) ? item.safeguardid.S : '';
                var prefix = id.split('-')[0].toUpperCase();
                return smbPrefixes.indexOf(prefix) !== -1;
            });
        }
        return rawItems;
    }

    /**
     * After fetchDataAndCalculateScores{NATIVE} populated processedItems and
     * native qualityData.Combined, re-score for VIEW year using
     * remapProcessedItemsForTargetYear + target quality JSON — mirrors the
     * CRFS cross-version branch in web-assessment-charts-crfs2021/22/23.js.
     */
    function runCrossVersionRescore(nativeYear, viewYear, viewFrameworkSlug) {
        var remapFn = WebAssessmentCharts.remapProcessedItemsForTargetYear;
        if (typeof remapFn !== 'function') {
            return Promise.reject(new Error(
                'remapProcessedItemsForTargetYear is not available on WebAssessmentCharts'));
        }
        var processed = WebAssessmentCharts.processedItems;
        var sourceCombined = WebAssessmentCharts.qualityData && WebAssessmentCharts.qualityData.Combined;
        if (!processed || !sourceCombined) {
            return Promise.reject(new Error(
                'Cross-version scoring needs processedItems and native qualityData.Combined'));
        }

        if (!WebAssessmentCharts._qualityDataCache) {
            WebAssessmentCharts._qualityDataCache = {};
        }

        var cacheKey = String(viewYear);
        var ensureTargetQuality = WebAssessmentCharts._qualityDataCache[cacheKey]
            ? Promise.resolve(WebAssessmentCharts._qualityDataCache[cacheKey])
            : WebAssessmentCharts.fetchQualityData(cacheKey).then(function (data) {
                WebAssessmentCharts._qualityDataCache[cacheKey] = data;
                return data;
            });

        return ensureTargetQuality.then(function (targetQ) {
            if (!targetQ || !targetQ.CRF || !targetQ.CRF.Items) {
                throw new Error('Target quality data missing CRF.Items for year ' + cacheKey);
            }

            var remappedItems = remapFn(processed, sourceCombined, cacheKey);
            var rawTargetItems = targetQ.CRF.Items;
            if (cacheKey === '2026') {
                rawTargetItems = filter2026TargetItemsBySlug(rawTargetItems, viewFrameworkSlug);
            }
            var targetQualityItems = WebAssessmentCharts.sortAndOrganizeItems(rawTargetItems);
            WebAssessmentCharts.qualityItems = targetQualityItems;
            WebAssessmentCharts.assessmentYear = cacheKey;

            var viewFw = String(viewFrameworkSlug || '').toLowerCase();
            WebAssessmentCharts.frameworkSlug = viewFw;

            if (isLegacyAssessmentYear(cacheKey)) {
                var legacyPriorityMap = WebAssessmentCharts.getLegacyPriorityMap
                    ? WebAssessmentCharts.getLegacyPriorityMap(cacheKey, targetQ)
                    : null;
                var scoresByTopicLegacy = WebAssessmentCharts.calculateScoresByTopicAndPriority(
                    remappedItems, targetQualityItems, true, legacyPriorityMap);
                var categorizedLegacy = WebAssessmentCharts.categorizeScores(
                    scoresByTopicLegacy, targetQualityItems, remappedItems, true);
                var maturityLegacy = WebAssessmentCharts.calculateMaturityScoresByLevelPerCategory(
                    categorizedLegacy, true);
                WebAssessmentCharts.categorizedScoresCopy = maturityLegacy.cleanedCategorizedScores;
                var overallLegacy = WebAssessmentCharts.calculateOverallMaturityScores(
                    maturityLegacy.results, true);
                WebAssessmentCharts.crfMaturityLevelScores(overallLegacy, true);

                var scoresByPrefixLegacy = WebAssessmentCharts.calculateScoresByMappingPrefix(
                    remappedItems, targetQ.Combined);
                WebAssessmentCharts.scoresByMappingPrefix = scoresByPrefixLegacy;
                if (scoresByPrefixLegacy && scoresByPrefixLegacy.cccAverages) {
                    WebAssessmentCharts.updateDomainScoresInTable('legacy-technical', scoresByPrefixLegacy.cccAverages);
                    WebAssessmentCharts.updateDomainScoresInTable('legacy-scope', scoresByPrefixLegacy.cccAverages);
                }
                WebAssessmentCharts.crfDomain(remappedItems, targetQualityItems, true);
                WebAssessmentCharts.crfScope(WebAssessmentCharts.categorizedScoresCopy, targetQualityItems);
            } else {
                var scoresByTopic = WebAssessmentCharts.calculateScoresByTopicAndPriority(
                    remappedItems, targetQualityItems);
                var categorized = WebAssessmentCharts.categorizeScores(
                    scoresByTopic, targetQualityItems, remappedItems);
                var maturityResult = WebAssessmentCharts.calculateMaturityScoresByLevelPerCategory(categorized);
                var overallScores = WebAssessmentCharts.calculateOverallMaturityScores(maturityResult.results);
                WebAssessmentCharts.categorizedScoresCopy = maturityResult.cleanedCategorizedScores;
                WebAssessmentCharts.potentialItemCounts = maturityResult.potentialItemCounts;
                WebAssessmentCharts.crfImplementationMaturity(maturityResult.cleanedCategorizedScores);
                WebAssessmentCharts.crfMaturityLevelScores(overallScores);
                WebAssessmentCharts.crfScope(maturityResult.cleanedCategorizedScores, targetQualityItems);

                var scoresByMappingPrefix = WebAssessmentCharts.calculateScoresByMappingPrefix(
                    remappedItems, targetQ.Combined, cacheKey);
                WebAssessmentCharts.scoresByMappingPrefix = scoresByMappingPrefix;
                var crfAvg = (scoresByMappingPrefix && scoresByMappingPrefix.crfAverages)
                    ? scoresByMappingPrefix.crfAverages : {};
                var cccAvg = (scoresByMappingPrefix && scoresByMappingPrefix.cccAverages)
                    ? scoresByMappingPrefix.cccAverages : {};
                var domainTableData = Object.keys(crfAvg).length > 0 ? crfAvg : cccAvg;
                WebAssessmentCharts.updateDomainScoresInTable('technical', domainTableData);
                WebAssessmentCharts.crfDomain(remappedItems, targetQualityItems, false);
            }

            console.log('[home-dashboard-dispatch] Cross-version rescore complete (native ' + nativeYear +
                ' → view ' + cacheKey + ').');
        });
    }

    /**
     * The .dashboard-selection switch case in web-assessment-charts-crfs2026.js
     * keeps a year-and-subversion-aware row filter. We can't reach the local
     * `viewResultsDomainClassesBySubversion` object inside that file's IIFE,
     * so copy the same allow-lists here. Keep in sync with crfs2026.js if
     * sub-version domain coverage ever changes.
     */
    var SUBVERSION_DOMAIN_ALLOWLIST = {
        'crf-s-appsec-2026': [
            'crf-domain-software-development-standardization-management',
            'crf-domain-software-development-operations-devops',
            'crf-domain-software-development-vulnerability-management'
        ],
        'crf-s-gov-2026': [
            'crf-domain-program-management',
            'crf-domain-safeguard-selection-management',
            'crf-domain-education-management',
            'crf-domain-safeguard-implementation-management',
            'crf-domain-safeguard-validation-management',
            'crf-domain-third-party-risk-management',
            'crf-domain-risk-communication-management'
        ],
        'crf-s-hyg-2026': [
            'crf-domain-asset-management',
            'crf-domain-resilience-management',
            'crf-domain-physical-security-management',
            'crf-domain-identity-management',
            'crf-domain-privileged-account-management',
            'crf-domain-access-management',
            'crf-domain-log-management',
            'crf-domain-system-protection-management',
            'crf-domain-software-management',
            'crf-domain-vulnerability-management',
            'crf-domain-network-device-management',
            'crf-domain-perimeter-network-access-management',
            'crf-domain-internal-network-access-management',
            'crf-domain-email-management',
            'crf-domain-software-development-vulnerability-management'
        ],
        'crf-s-smb-2026': [
            'crf-domain-safeguard-selection-management',
            'crf-domain-education-management',
            'crf-domain-asset-management',
            'crf-domain-resilience-management',
            'crf-domain-physical-security-management',
            'crf-domain-identity-management',
            'crf-domain-privileged-account-management',
            'crf-domain-access-management',
            'crf-domain-log-management',
            'crf-domain-system-protection-management',
            'crf-domain-software-management',
            'crf-domain-vulnerability-management',
            'crf-domain-network-device-management',
            'crf-domain-perimeter-network-access-management',
            'crf-domain-internal-network-access-management',
            'crf-domain-cloud-service-provider-management',
            'crf-domain-email-management'
        ]
    };

    /**
     * Map framework taxonomy slug → panel root selector inside the home widget.
     */
    var HOME_FW_PANEL_SELECTOR = {
        'cis-5-0': '.cis5-container',
        'cis-6-0': '.cis6-container',
        'cis-7-0': '.cis7-container',
        'cis-7-1': '.cis7-container',
        'cis-8-0': '.cis8-container',
        'cis-8-1': '.cis81-container',
        'nist-csf-1-0': '.nist10-container',
        'nist-csf-1-1': '.nist11-container',
        'nist-csf-2-0': '.nist2-container',
        'nycrr-500-v2': '.nycrr500-container',
        'iso-27002-2013': '.iso2013-container',
        'iso-27002-2022': '.iso2022-container',
        'fca-rm-2024': '.fcarm2024-container',
        'uc-policy-is-3': '.ucis3-container',
        'nist-800-171-rev3': '.nist-800-171-rev3-container',
        'pci-dss-4-0': '.pci-dss-container',
        'sa-nca-ecc-1-0': '.sa-nca-ecc-container',
        'sama-crfr-1-0': '.sama-crfr-container'
    };

    function ensureHomeProjectChartsRowVisible() {
        var $w = jQuery('.home-project-dashboard-widget');
        if (!$w.length) {
            return;
        }
        $w.find('.select-a-project, .select-a-dashboard').hide();
        $w.find('.home-project-dashboard-results-panel').removeClass('d-none');
        $w.find('.home-project-dashboard-benchmark').removeClass('d-none');
        $w.find('.home-project-dashboard-charts-row').removeClass('d-none');
        $w.find('.home-project-dashboard-domain-col').show();
    }

    /** Every third-party panel root under the home widget (for hard reset before show). */
    var HOME_FW_ALL_PANEL_ROOTS =
        '.cis5-container,.cis6-container,.cis7-container,.cis8-container,.cis81-container,' +
        '.nist10-container,.nist11-container,.nist2-container,.nycrr500-container,' +
        '.iso2013-container,.iso2022-container,.fcarm2024-container,.ucis3-container,' +
        '.nist-800-171-rev3-container,.pci-dss-container,.sa-nca-ecc-container,.sama-crfr-container';

    /**
     * After a non-CRF home fetch (NIST/CIS/NYCRR), show the matching markup
     * and hide CRF + other framework panels.
     */
    WebAssessmentCharts.homeDashboardActivateFrameworkPanels = function (frameworkSlug) {
        var slug = String(frameworkSlug || '').toLowerCase();
        var sel = HOME_FW_PANEL_SELECTOR[slug];
        var $w = jQuery('.home-widget-project-dashboard');
        if (!$w.length) return;
        $w.find('.crf-container, .crf-legacy-container').hide();
        $w.find('.home-dash-fw-panel').hide();
        $w.find(HOME_FW_ALL_PANEL_ROOTS).hide();
        ensureHomeProjectChartsRowVisible();
        $w.find('.home-dashboards-wrapper').show();
        if (sel) {
            $w.find(sel).css('display', 'block').show();
        } else {
            console.warn('[home-dashboard-dispatch] No home panel mapped for framework slug:', slug);
        }
    };

    /**
     * Public API for js/home-dashboard.js.
     *
     * @returns {Promise<{ kind: 'crfs', year, frameworkSlug } | { kind: 'framework', frameworkSlug }>}
     */
    WebAssessmentCharts.homeDashboardFetchForProject = function (projectSlug, nativeFrameworkSlug, viewFrameworkSlug) {
        var nativeFw = String(nativeFrameworkSlug || '').toLowerCase();
        var viewFw = String(viewFrameworkSlug || nativeFw).toLowerCase();

        var nativeYear = frameworkSlugToYear(nativeFw);
        var viewYear = frameworkSlugToYear(viewFw);

        // —— Third-party frameworks (registry), not CRF-S calendar years ——
        if (!nativeYear) {
            if (typeof WebAssessmentCharts.homeDashboardHasFetcher !== 'function' ||
                !WebAssessmentCharts.homeDashboardHasFetcher(nativeFw)) {
                return Promise.reject(new Error(
                    'Unsupported project framework (no home fetcher): ' + nativeFw));
            }
            if (nativeFw !== viewFw) {
                return Promise.reject(new Error(
                    'On the home dashboard, third-party frameworks cannot be compared across different standards — select the project\'s own framework.'));
            }
            console.log('[home-dashboard-dispatch] Registry fetch for', nativeFw, projectSlug);
            return WebAssessmentCharts.homeDashboardFetchNonCrfs(projectSlug, nativeFw)
                .then(function () {
                    ensureHomeProjectChartsRowVisible();
                    WebAssessmentCharts.frameworkSlug = nativeFw;
                    return { kind: 'framework', frameworkSlug: nativeFw };
                });
        }

        if (!viewYear) {
            viewYear = nativeYear;
            viewFw = nativeFw;
        }

        var fnName = 'fetchDataAndCalculateScores' + nativeYear;
        var fn = WebAssessmentCharts[fnName];
        if (typeof fn !== 'function') {
            return Promise.reject(new Error(
                'WebAssessmentCharts.' + fnName + ' is not defined. ' +
                'Confirm web-assessment-charts-crfs' + nativeYear + '.js is in the home-dashboard bundle.'
            ));
        }

        WebAssessmentCharts.frameworkSlug = nativeFw;

        var needsCrossVersion = (nativeYear !== viewYear) ||
            (nativeYear === '2026' && viewYear === '2026' && nativeFw !== viewFw);

        console.log('[home-dashboard-dispatch] Native fetch ' + fnName + '(' + projectSlug + ')',
            'nativeFw=' + nativeFw, 'viewFw=' + viewFw,
            needsCrossVersion ? '(cross-version rescore after fetch)' : '(same view)');

        return Promise.resolve(fn.call(WebAssessmentCharts, projectSlug))
            .then(function () {
                ensureHomeProjectChartsRowVisible();
                if (!needsCrossVersion) {
                    WebAssessmentCharts.frameworkSlug = viewFw;
                    return { kind: 'crfs', year: viewYear, frameworkSlug: viewFw };
                }
                return runCrossVersionRescore(nativeYear, viewYear, viewFw).then(function () {
                    WebAssessmentCharts.frameworkSlug = viewFw;
                    return { kind: 'crfs', year: viewYear, frameworkSlug: viewFw };
                });
            });
    };

    /**
     * Render only the maturity-by-domain chart for the home widget. The
     * matching year's fetchDataAndCalculateScores has already populated the
     * `.crf-domain-*` (modern) or `.crf-legacy-domain-*` (legacy) table
     * cells via WebAssessmentCharts.crfDomain(…) inside its body, so the
     * table is ready to be read by createChartFromTable.
     *
     * The year files normally apply year/sub-version-conditional row
     * visibility (AI for 2025+, SDO for 2026, sub-version allow-lists) from
     * their .dashboard-selection switch case — but the dispatcher's detach
     * stripped those listeners, so we replicate the relevant bits here.
     *
     * @param {string} year          '2021'..'2026'
     * @param {string} [frameworkSlug] Same slug passed to fetch. Optional;
     *                                  only used to filter rows for 2026
     *                                  sub-versions (AppSec/Gov/Hyg/SMB).
     */
    WebAssessmentCharts.homeDashboardRenderTechnicalChart = function (year, frameworkSlug) {
        if (typeof WebAssessmentCharts.createChartFromTable !== 'function') {
            console.error('[home-dashboard-dispatch] createChartFromTable is not loaded.');
            return null;
        }
        var charts = WebAssessmentCharts.__homeDashboardCharts =
            WebAssessmentCharts.__homeDashboardCharts || {};
        var fwSlug = String(frameworkSlug || '').toLowerCase();
        var isLegacy = (year === '2021' || year === '2022' || year === '2023');
        var tableId  = isLegacy ? 'legacy-technical' : 'technical';
        var canvasId = isLegacy ? 'crf-legacy-technical-chart' : 'crf-technical-chart';

        var $widget = jQuery('.home-widget-project-dashboard');
        if ($widget.length) {
            ensureHomeProjectChartsRowVisible();
            $widget.find('.home-dashboards-wrapper').show();
            $widget.find('.home-dash-fw-panel').hide();
            $widget.find(HOME_FW_ALL_PANEL_ROOTS).hide();

            // Make sure the matching container is visible so Chart.js can
            // size the canvas. The year-specific scripts normally do this
            // from their .dashboard-selection switch case.
            if (isLegacy) {
                $widget.find('.crf-container').hide();
                $widget.find('.crf-legacy-container').css('display', 'block').show();
            } else {
                $widget.find('.crf-legacy-container').hide();
                $widget.find('.crf-container').css('display', 'block').show();
            }

            // Reset row visibility — a prior pick (e.g. a SMB-2026 project)
            // could have collapsed unrelated rows; restore the baseline
            // before applying year/sub-version filters again. Rows that
            // crfDomain flagged as all-NA stay hidden.
            $widget.find('#technical tbody tr:not(.domain-all-na)').show();

            if (!isLegacy) {
                // AI Management category was introduced in 2025. SDO
                // (Software Development Operations Management) in 2026 only.
                var yearNum = parseInt(year, 10);
                var showAi  = yearNum >= 2025;
                var showSdo = yearNum >= 2026;
                $widget.find('.ai-management-row').toggle(showAi);
                $widget.find('.sdo-management-row').toggle(showSdo);

                // 2026 sub-versions: show only domains relevant to the
                // sub-version (mirrors viewResultsDomainClassesBySubversion
                // in web-assessment-charts-crfs2026.js).
                var allowed = SUBVERSION_DOMAIN_ALLOWLIST[fwSlug];
                if (allowed) {
                    console.log('[home-dashboard-dispatch] Applying sub-version filter for ' + fwSlug,
                        '(' + allowed.length + ' allowed domains).');
                    $widget.find('#technical tbody tr').each(function () {
                        var $row = $(this);
                        if ($row.hasClass('domain-all-na')) {
                            $row.hide();
                            return;
                        }
                        var $cell = $row.find('td[class^="crf-domain-"]');
                        if (!$cell.length) return;
                        var cellClass = ($cell.attr('class') || '').split(/\s+/).find(function (c) {
                            return c.indexOf('crf-domain-') === 0;
                        });
                        if (!cellClass) return;
                        $row.toggle(allowed.indexOf(cellClass) !== -1);
                    });
                }
            }
        }

        console.log('[home-dashboard-dispatch] Rendering ' + canvasId + ' from #' + tableId,
            '(year=' + year + ', isLegacy=' + isLegacy + ')');
        WebAssessmentCharts.createChartFromTable(tableId, canvasId, charts, true);
        var chartInstance = charts[canvasId] || null;
        if (chartInstance && typeof chartInstance.resize === 'function') {
            window.requestAnimationFrame(function () {
                chartInstance.resize();
            });
        }
        return chartInstance;
    };

    /**
     * The year files attach `.project-selection` and `.dashboard-selection`
     * change listeners during their own $(document).ready. We want exactly
     * one set of handlers on the home page (defined in js/home-dashboard.js),
     * so the bundle's listeners must come off after they've all registered.
     *
     * We can't just call `$el.off('change')` — that also strips Select2's
     * internal `change.select2` listener, which Select2 uses to keep its
     * rendered tag in sync. The user then picks an option, the underlying
     * `<select>` updates, but the visible Select2 tag stays on the
     * "Select Project" / "Select Dashboard" placeholder forever.
     *
     * Cleanest fix: destroy Select2 (which releases all its event bindings),
     * .off('change') to drop the year-file plain handlers, then re-init
     * Select2 with the same options it had. After this the only `change`
     * handlers on these selects are Select2's freshly-re-attached ones; the
     * year files don't run anymore on user interaction.
     *
     * We also neutralize WebAssessmentCharts.fetchDataAndCalculateScores
     * (last assignment wins after the bundle loaded — that's the 2026
     * version) so anything still trying to call the un-suffixed function
     * harmlessly resolves instead of fetching against the wrong year's
     * quality JSON.
     */
    function detachBundleSelectionHandlers() {
        var $selects = $('.project-selection, .dashboard-selection');
        if (!$selects.length) return;

        var detached = 0;
        $selects.each(function () {
            var $el = $(this);
            var hadSelect2 = !!$.fn.select2 && $el.hasClass('select2-hidden-accessible');
            var select2Options = null;
            if (hadSelect2) {
                // $el.data('select2').options.options is the same opts object the
                // year files passed into $.fn.select2 — re-using it preserves
                // width / templateResult / dropdownParent.
                var inst = $el.data('select2');
                if (inst && inst.options && inst.options.options) {
                    select2Options = $.extend(true, {}, inst.options.options);
                }
                try { $el.select2('destroy'); } catch (e) {
                    console.warn('[home-dashboard-dispatch] select2 destroy failed', e);
                }
            }

            // Now safe — Select2's handlers are gone. Only the year files'
            // plain `change` bindings remain.
            $el.off('change');
            detached++;

            if (hadSelect2) {
                $el.select2(select2Options || { width: '100%' });
            }
        });

        WebAssessmentCharts.fetchDataAndCalculateScores = function (slug) {
            console.log('[home-dashboard-dispatch] fetchDataAndCalculateScores no-op for', slug,
                '— use homeDashboardFetchForProject instead.');
            return Promise.resolve();
        };

        console.log('[home-dashboard-dispatch] Detached year-specific change handlers from ' +
            detached + ' select(s) and neutralized the un-suffixed fetchDataAndCalculateScores alias.');
    }

    // Each year file's $(document).ready callback registers its handlers in
    // load order. Run AFTER all of them so .off('change') strips them all.
    // setTimeout(0) is the simplest "after current microtask flush" trick that
    // works regardless of how many ready callbacks the bundle queued.
    $(function () {
        window.setTimeout(detachBundleSelectionHandlers, 0);
    });
})(jQuery);
